// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AppConfig } from "../../config/config";
import { SnackMessage, httpStatus } from "../../config/constant";
import { PublicAPIService } from "../../utils/publicApiService";
import { VerificationState, CertificationsPayload, AwardsPayload } from "../../utils/types";
import { enqueueSnackbarMessage } from "../commonSlice/common";

const initialState: VerificationState = {
  state: "idle",
  stateMessage: null,
  certifications: [],
  awards: [],
};

export const getVerificationRequest = createAsyncThunk(
  "certificate/getVerification",
  async (certificationId: string, { dispatch }) => {
    return new Promise<{ certifications: CertificationsPayload[] }>(
      (resolve, reject) => {
        PublicAPIService.getInstance()
          .get(
            AppConfig.serviceUrls.getAllAwardedCertifications +
              "/?userCertificationId=" +
              certificationId
          )
          .then((resp) => {
            if (resp.status == 200) {
              resolve({ certifications: resp.data });
            } else {
              reject("Something went wrong");
            }
          })
          .catch((error: Error) => {
            dispatch(
              enqueueSnackbarMessage({
                message: SnackMessage.error.certificationVerification,
                type: "error",
              })
            );
            reject(error);
          });
      }
    );
  }
);

export const getAwardsVerificationRequest = createAsyncThunk(
  "certificate/getAwardsVerification",
  async (certificationId: string, { dispatch }) => {
    return new Promise<{ awards: AwardsPayload[] }>(
      (resolve, reject) => {
        PublicAPIService.getInstance()
          .get(`${AppConfig.serviceUrls.getAwards}/?certificationId=${certificationId}`)
          .then((resp) => {
            if (resp.status == 200) {
              resolve({ awards: resp.data });
            } else {
              reject("Something went wrong");
            }
          })
          .catch((error: Error) => {
            dispatch(
              enqueueSnackbarMessage({
                message: SnackMessage.error.certificationVerification,
                type: "error",
              })
            );
            reject(error);
          });
      }
    );
  }
);

const AppSlice = createSlice({
  name: "certificate",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getVerificationRequest.pending, (state, action) => {
        state.stateMessage = "Loading Certification Portal";
        state.state = "loading";
      })
      .addCase(getVerificationRequest.fulfilled, (state, action) => {
        state.certifications = action.payload.certifications;
        state.stateMessage = "Fulfilled Certification Portal";
        state.state = "success";
      })
      .addCase(getVerificationRequest.rejected, (state, action) => {
        state.stateMessage = "Request failed";
        state.state = "failed";
      })
      .addCase(getAwardsVerificationRequest.pending, (state, action) => {
        state.stateMessage = "Loading Certification Portal";
        state.state = "loading";
      })
      .addCase(getAwardsVerificationRequest.fulfilled, (state, action) => {
        state.awards = action.payload.awards;
        state.stateMessage = "Fulfilled Certification Portal";
        state.state = "success";
      })
      .addCase(getAwardsVerificationRequest.rejected, (state, action) => {
        state.stateMessage = "Request failed";
        state.state = "failed";
      });
  },
});

export default AppSlice.reducer;
